import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { useTranslation } from 'react-i18next';
import { Box, Typography } from "@mui/material";
import { useContext } from "react";
import { AppContext } from "/@/app/AppContext";

const itemsToShow = {
  "life_expectancy_no_plan": {
    // name: "ОПЖ без выполнения плана",
    name: "ОПЖ",
    color: "#057ab2",
    order: 2,
  },

  "life_expectancy_by_plan_healthy": {
    // name: "Здоровая ОПЖ при выполнении плана",
    name: "Здоровая ОПЖ",
    color: "#5ebf08",
    order: 3,
  },

  "life_expectancy_by_plan": {
    // name: "ОПЖ при выполнении плана",
    name: "ОПЖ",
    color: "#f4952d",
    order: 4,
  },
}


export const LifeExpectancyChart = () => {

  //
  // Prepare data
  //
    const { t } = useTranslation();
  const { panel } = useContext(AppContext).bootstrapData;
  const data = Object.keys(itemsToShow).map(key => ({
    color: itemsToShow[key].color,
    name: t(itemsToShow[key].name),
    order: itemsToShow[key].order,
    value: panel[key],
  })).sort((a, b) => a.order - b.order);

  if (!data.some(d => d.value > 0)) {
    return null;
  }

  data.unshift({
    // name: "Здоровая ОПЖ без выполнения плана",
    name: t("Здоровая ОПЖ"),
    color: "#05b2b2",
    order: 1,
    value:  Math.round(panel.life_expectancy_no_plan * 0.87),
  });

  //
  // Chart refresh
  //
  const ref = useRef();
  const ref2 = useRef();

  useEffect(() => {
    const updateSvg = () => { rebuildDiagram(ref, ref2, data) };
    updateSvg();

    const observer = new ResizeObserver(() => updateSvg());
    observer.observe(ref.current);
    observer.observe(ref2.current);

    window.addEventListener('resize', updateSvg);
    return () => {
      window.removeEventListener('resize', updateSvg);
      observer.disconnect();
    }
  }, []);

  //
  // Display chart and legend
  //
  return (
      <Box sx={{ display: "flex", flexFlow: "column nowrap", width:"100%"}}>
        <Typography variant="h6" sx={{textAlign:"center"}}>{t("Без выполнении плана")}</Typography>
        <svg ref={ref}></svg>
        <Box sx={{ display: "flex", flexFlow: "row wrap", justifyContent: "center", columnGap: "10px", rowGap: "10px", mb: 3}}>
          {data.slice(0,2).map((item, index) => (
            <Box key={index} sx={{ display: "flex", flexFlow: "row nowrap", alignItems: "left", columnGap: "10px"}}>
              <Box sx={{ width: "20px", height: "20px", backgroundColor: item.color, borderRadius: "50%"}}></Box>
              <Typography variant="body2">{item.name}</Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="h6" sx={{textAlign:"center"}}>{t("При выполнении плана")}</Typography>
        <svg ref={ref2}></svg>
        <Box sx={{ display: "flex", flexFlow: "row wrap", justifyContent: "center", columnGap: "10px", rowGap: "10px"}}>
          {data.slice(2,4).map((item, index) => (
            <Box key={index} sx={{ display: "flex", flexFlow: "row nowrap", alignItems: "left", columnGap: "10px"}}>
              <Box sx={{ width: "20px", height: "20px", backgroundColor: item.color, borderRadius: "50%"}}></Box>
              <Typography variant="body2">{item.name}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
  )
};



const rebuildDiagram = (ref, ref2, data) => {
  _diagram(ref, data.slice(0, 2), 1);
  _diagram(ref2, data.slice(2, 4), 2);
}
const _diagram = (ref, data, index) => {
  d3.select(ref.current).selectAll('*').remove(); // Clear existing content
  //
  // XXX the code is written mostly by GPT
  //
  const margin = { top: 30, right: 10, bottom: 10, left: 5 };
  let width = parseInt(d3.select(ref.current).style('width')) - margin.left - margin.right;
  const baseHeight = 190
  const height = baseHeight - margin.top - margin.bottom;

  const svg = d3.select(ref.current)
      .attr("width", '100%')
      .attr("height", baseHeight)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);


  const xScale = d3.scaleLinear()
      .domain([0, 100])
      .range([0, width]);

  const yScale = d3.scaleBand()
      .domain(data.map(d => d.name))
      .range([0, height])
      .padding(0.1);

  // Add grey vertical lines for each 10 years
  svg.selectAll(".grid-line")
      .data(d3.range(10, 101, 10))
      .enter()
      .append("line")
      .attr("class", "grid-line")
      .attr("x1", d => xScale(d))
      .attr("x2", d => xScale(d))
      .attr("y1", 0)
      .attr("y2", height)
      .attr("stroke", "#ccc")
      .attr("stroke-width", 0.5);

  // Define the gradient for each bar
  data.forEach((d, i) => {
    let gradientId = `gradient${i}-${index}`;

    svg.append("defs")
        .append("linearGradient")
        .attr("id", gradientId)
        .attr("x1", "0%")
        .attr("y1", "0%")
        .attr("x2", "100%")
        .attr("y2", "0%")
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", d.color)
        .attr("stop-opacity", 0.1);

    svg.select(`#${gradientId}`)
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", d.color)
        .attr("stop-opacity", 1);

    d.gradientId = gradientId;
  });

  // Add bars
  svg.selectAll(".bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", 0)
      .attr("y", d => yScale(d.name))
      .attr("width", d => xScale(d.value))
      .attr("height", yScale.bandwidth())
      .attr("fill", d => `url(#${d.gradientId})`);


  // Add white boxes with text inside the end of each bar
  const boxWidth = 40;
  svg.selectAll(".label")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "label")
      .attr("x", d => xScale(d.value) - boxWidth - 10)
      .attr("y", d => yScale(d.name) + yScale.bandwidth() / 4)
      .attr("width", boxWidth)
      .attr("height", yScale.bandwidth() / 2)
      .attr("fill", "white");

  svg.selectAll(".label-text")
      .data(data)
      .enter()
      .append("text")
      .attr("class", "label-text")
      .attr("x", d => xScale(d.value) - 25)
      .attr("y", d => yScale(d.name) + yScale.bandwidth() / 1.65)
      .attr("text-anchor", "middle")
      .attr("fill", d => d.color)
      .text(d => d.value);

  // Add x axis
  const xAxis = d3.axisTop(xScale).ticks(10);
  svg.append("g")
    .attr("class", "x axis")
    .call(xAxis)
    .selectAll("text")
    .attr("fill", "grey"); // Change label color to grey

  // Change the color of the axis lines and ticks to grey
  svg.selectAll(".x.axis path")
      .attr("stroke", "grey");

  svg.selectAll(".x.axis line")
      .attr("stroke", "grey");
};
